/**
 * MessageBubble - Individual message display component.
 *
 * Renders user or assistant messages with:
 * - Avatar icons
 * - Markdown content rendering
 * - Tool call timeline integration
 * - Suggested actions
 * - Timestamp display
 * - Optional voice source indicator
 * - Attachment display for images and files
 */

import { Bot, User, Mic } from 'lucide-react'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import { ToolTimeline } from '../ToolDisplay/ToolTimeline'
import { SuggestedActions } from '../SuggestedActions'
import type { Message } from '@/store/chatStore'

interface MessageBubbleProps {
  message: Message
  onActionComplete?: (action: { label: string }, response: unknown) => void
}

export function MessageBubble({ message, onActionComplete }: MessageBubbleProps) {
  const isUser = message.role === 'user'

  return (
    <div
      className={`flex ${isUser ? 'justify-end' : 'justify-start'} message-enter`}
    >
      <div className="flex items-start gap-2 max-w-[90%] sm:max-w-[80%]">
        {/* Avatar for assistant */}
        {!isUser && (
          <div className="flex-shrink-0 w-8 h-8 rounded-lg bg-gradient-to-br from-accent/20 to-accent/5 flex items-center justify-center mt-1">
            <Bot className="w-4 h-4 text-accent" />
          </div>
        )}

        <div
          className={`rounded-2xl px-4 py-3 ${
            isUser
              ? 'bg-gradient-to-br from-accent to-accent-hover text-white'
              : 'bg-surface border border-surface-border text-text'
          }`}
        >
          {/* Voice source indicator */}
          {message.isVoice && (
            <div className="flex items-center gap-1 text-xs opacity-60 mb-1">
              <Mic className="w-3 h-3" />
              <span>Voice</span>
            </div>
          )}

          {/* Message content with markdown rendering */}
          <div className="prose prose-sm prose-invert max-w-none markdown-content">
            <ReactMarkdown remarkPlugins={[remarkGfm]}>
              {message.content}
            </ReactMarkdown>
          </div>

          {/* Inline image attachments */}
          {message.attachments && message.attachments.length > 0 && (
            <div className="mt-3 space-y-2">
              {message.attachments
                .filter((a) => a.type === 'image')
                .map((attachment, i) => (
                  <div key={i} className="rounded-lg overflow-hidden max-w-sm">
                    <img
                      src={attachment.url}
                      alt={attachment.filename}
                      className="w-full h-auto max-h-64 object-cover cursor-pointer hover:opacity-90 transition-opacity"
                      onClick={() => window.open(attachment.url, '_blank')}
                    />
                  </div>
                ))}
              {/* Non-image attachments as chips */}
              {message.attachments
                .filter((a) => a.type !== 'image')
                .map((attachment, i) => (
                  <a
                    key={i}
                    href={attachment.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-black/20 text-xs hover:bg-black/30 transition-colors"
                  >
                    <span className="truncate max-w-[150px]">{attachment.filename}</span>
                    {attachment.size && (
                      <span className="text-text-muted">
                        ({formatFileSize(attachment.size)})
                      </span>
                    )}
                  </a>
                ))}
            </div>
          )}

          {/* Tool calls - timeline display */}
          {message.toolCalls && message.toolCalls.length > 0 && (
            <div className="mt-3 pt-3 border-t border-white/10">
              <ToolTimeline toolCalls={message.toolCalls} />
            </div>
          )}

          {/* Suggested actions */}
          {message.suggestedActions && (
            <SuggestedActions
              actions={message.suggestedActions}
              onActionComplete={(action, response) => {
                onActionComplete?.(action, response)
              }}
            />
          )}

          <div className="text-xs opacity-50 mt-2">
            {message.timestamp.toLocaleTimeString()}
          </div>
        </div>

        {/* Avatar for user */}
        {isUser && (
          <div className="flex-shrink-0 w-8 h-8 rounded-lg bg-accent/20 flex items-center justify-center mt-1">
            <User className="w-4 h-4 text-accent" />
          </div>
        )}
      </div>
    </div>
  )
}

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes}B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)}KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)}MB`
}

export default MessageBubble
